import dayjs from "dayjs";

/**
 * Month ranges for the date filter. A month is 'YYYY-MM'; the filter sends whole months as
 * ['YYYY-MM-01', 'YYYY-MM-<last day>'].
 */

export type Month = string;
export type MonthRange = [Month, Month];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const monthOf = (year: number, monthIndex: number): Month => `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
const parts = (m: Month): [number, number] => [Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1];
export const monthName = (m: Month) => MONTH_NAMES[parts(m)[1]];

/** The months of a date range, or null when it is not set */
export function toMonthRange(dateRange: [string, string] | undefined): MonthRange | null {
    if (!dateRange) return null;
    return [dateRange[0].slice(0, 7), dateRange[1].slice(0, 7)];
}

/** The date range covering whole months */
export function toDateRange([from, to]: MonthRange): [string, string] {
    const [a, b] = from <= to ? [from, to] : [to, from];
    return [`${a}-01`, dayjs(`${b}-01`).endOf('month').format('YYYY-MM-DD')];
}

/** Short label: "2025", "Q1 2025", "Mar 2025", "Jan–Jun 2025" or "Nov 2024 – Feb 2025" */
export function formatMonthRange(range: MonthRange | null): string {
    if (!range) return 'Any';
    const [[y1, m1], [y2, m2]] = [parts(range[0]), parts(range[1])];
    if (y1 === y2) {
        if (m1 === 0 && m2 === 11) return String(y1);
        if (m1 % 3 === 0 && m2 === m1 + 2) return `Q${m1 / 3 + 1} ${y1}`;
        if (m1 === m2) return `${MONTH_NAMES[m1]} ${y1}`;
        return `${MONTH_NAMES[m1]}–${MONTH_NAMES[m2]} ${y1}`;
    }
    return `${MONTH_NAMES[m1]} ${y1} – ${MONTH_NAMES[m2]} ${y2}`;
}

export type MonthRangePreset = {label: string, range: MonthRange | null};

/**
 * Presets for spend analysis. `today` is a parameter so they can be tested;
 * `defaultRange` is the dataset's default period (DATA_YEAR_START), when there is one.
 */
export function monthRangePresets(today: dayjs.Dayjs, defaultRange: MonthRange | null, quarterYear: number): MonthRangePreset[] {
    const thisMonth = today.format('YYYY-MM');
    const year = today.year();
    const presets: MonthRangePreset[] = [];
    if (defaultRange) presets.push({label: `Dataset period (${formatMonthRange(defaultRange)})`, range: defaultRange});
    presets.push(
        {label: 'Last 12 months', range: [today.subtract(11, 'month').format('YYYY-MM'), thisMonth]},
        {label: 'Year to date', range: [monthOf(year, 0), thisMonth]},
        {label: `Previous year (${year - 1})`, range: [monthOf(year - 1, 0), monthOf(year - 1, 11)]},
        ...[0, 1, 2, 3].map(q => ({
            label: `Q${q + 1} ${quarterYear}`,
            range: [monthOf(quarterYear, q * 3), monthOf(quarterYear, q * 3 + 2)] as MonthRange,
        })),
        {label: 'All dates', range: null},
    );
    return presets;
}

/** Whether a month is inside a (possibly reversed) range */
export function inMonthRange(m: Month, range: [Month, Month | null] | null): boolean {
    if (!range) return false;
    const [a, b] = range[1] === null ? [range[0], range[0]] : range[0] <= range[1] ? [range[0], range[1]] : [range[1], range[0]];
    return m >= a && m <= b;
}

export const sameMonthRange = (a: MonthRange | null, b: MonthRange | null) =>
    (a === null && b === null) || (a !== null && b !== null && a[0] === b[0] && a[1] === b[1]);
